import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { useLanguage } from '../../context/LanguageContext';
import {
  CONTACT_EMAIL,
  CONTACT_PHONE_DISPLAY,
  CONTACT_PHONE_TEL,
  CONTACT_TELEGRAM_URL,
} from '../../config/siteConstants';

/** Persistent anchored CTA + slide-out tray — real Telegram, phone, email, contact form. */
export default function ContactPush() {
  const [open, setOpen] = useState(false);
  const { t } = useLanguage();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const goContactForm = (e) => {
    e.preventDefault();
    setOpen(false);
    if (pathname === '/') {
      document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate({ pathname: '/', hash: 'contact' });
    }
  };

  return (
    <div
      className="fixed z-[48] flex flex-col items-end gap-3"
      style={{
        right: 'max(1rem, env(safe-area-inset-right, 0px))',
        bottom: 'max(1.25rem, env(safe-area-inset-bottom, 0px))',
      }}
      data-testid="contact-push-root"
    >
      <AnimatePresence>
        {open && (
          <motion.button
            key="contact-push-backdrop"
            type="button"
            aria-label={t.contactPush.close}
            className="fixed inset-0 z-[46] bg-black/30 backdrop-blur-[3px] sm:bg-black/18"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={() => setOpen(false)}
          />
        )}
        {open && (
          <motion.div
            key="contact-push-panel"
            role="dialog"
            aria-modal="true"
            aria-label={t.contactPush.title}
            initial={reduceMotion ? false : { opacity: 0, x: 28, y: 10 }}
            animate={{ opacity: 1, x: 0, y: 0 }}
            exit={reduceMotion ? undefined : { opacity: 0, x: 24, y: 8 }}
            transition={{ duration: 0.34, ease: [0.16, 1, 0.3, 1] }}
            className="relative z-[47] w-[min(calc(100vw-2rem),19rem)] border border-foreground/14 bg-[hsl(var(--ivory-mist))] shadow-[0_24px_64px_rgba(0,0,0,0.2)]"
            data-testid="contact-push-panel"
          >
            <div className="border-b border-foreground/[0.08] px-5 py-4">
              <p className="text-[10px] tracking-[0.22em] uppercase text-foreground/40 font-body">{t.contactPush.kicker}</p>
              <p className="mt-1 font-display text-lg text-foreground leading-snug">{t.contactPush.title}</p>
            </div>
            <ul className="flex flex-col p-2">
              <li>
                <a
                  href={CONTACT_TELEGRAM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex min-h-[48px] items-center px-4 text-[13px] text-foreground/80 hover:bg-foreground/[0.06] hover:text-foreground transition-colors duration-200 rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-foreground/30"
                  data-testid="contact-push-telegram"
                >
                  {t.contactPush.telegram}
                </a>
              </li>
              <li>
                <a
                  href={CONTACT_PHONE_TEL}
                  className="flex min-h-[48px] items-center px-4 text-[13px] text-foreground/80 hover:bg-foreground/[0.06] hover:text-foreground transition-colors duration-200 rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-foreground/30"
                  data-testid="contact-push-phone"
                >
                  {CONTACT_PHONE_DISPLAY}
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${CONTACT_EMAIL}`}
                  className="flex min-h-[48px] items-center px-4 text-[13px] text-foreground/80 hover:bg-foreground/[0.06] hover:text-foreground transition-colors duration-200 rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-foreground/30"
                  data-testid="contact-push-email"
                >
                  {CONTACT_EMAIL}
                </a>
              </li>
              <li className="border-t border-foreground/[0.06] mt-1 pt-1">
                <Link
                  to="/#contact"
                  onClick={goContactForm}
                  className="flex min-h-[48px] items-center px-4 text-[12px] tracking-[0.12em] uppercase text-foreground font-medium hover:bg-foreground/[0.08] transition-colors duration-200 rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-foreground/30"
                  data-testid="contact-push-form"
                >
                  {t.contactPush.form}
                </Link>
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        whileTap={reduceMotion ? undefined : { scale: 0.98 }}
        className={`relative z-[49] min-h-[3.5rem] max-w-[min(calc(100vw-2rem),17rem)] rounded-full bg-foreground px-5 sm:px-7 text-[10px] sm:text-[11px] font-semibold tracking-[0.12em] sm:tracking-[0.14em] uppercase leading-tight text-[hsl(var(--ivory-mist))] text-center ring-2 ring-black/10 hover:opacity-95 transition-opacity duration-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-foreground ${!open && !reduceMotion ? 'sati-contact-fab--pulse' : 'shadow-[0_12px_40px_rgba(0,0,0,0.35)]'}`}
        data-testid="contact-push-fab"
      >
        {open ? t.contactPush.closeFab : t.contactPush.fab}
      </motion.button>
    </div>
  );
}
